import {useEffect, useMemo} from 'react';

import moment from 'moment-timezone';

import {replaceRoute} from '~/shared/router';
import {UserReportApiResponse} from '~/shared/services/apiService/models';

interface UseTransactionsRangeRedirectParams {
  dateBias: number;
  currentDateBiasValue?: UserReportApiResponse;
  buildUrlWithQuery: (dateBias: number) => string;
}

export const useTransactionsRangeRedirect = ({
  dateBias,
  currentDateBiasValue,
  buildUrlWithQuery,
}: UseTransactionsRangeRedirectParams) => {
  /**
   * Checks if the user is viewing a month outside their valid range.
   * Returns true if viewing a future month (dateBias > 0) or a month before their first order.
   */
  const isOutOfRange = useMemo(() => {
    if (dateBias > 0) {
      return true;
    }

    if (!currentDateBiasValue || dateBias !== currentDateBiasValue.dateBias) {
      return false;
    }
      
    const {userCreated} = currentDateBiasValue || {};
    
    if (!userCreated && dateBias !== 0) {
      return true;
    }

    const lastDateOfPeriod = moment(currentDateBiasValue.companyReportRange.endDateStr, 'DD.MM.YYYY').add(1, 'day');
    const userCreationDate = moment(userCreated);

    return lastDateOfPeriod.isBefore(userCreationDate, 'second');
  }, [dateBias, currentDateBiasValue]);

  // Effect: Redirect to current month if user is out of range
  useEffect(() => {
    if (isOutOfRange) {
      replaceRoute(buildUrlWithQuery(0), {keepAllQueries: false});
    }
  }, [isOutOfRange, buildUrlWithQuery]);
};
